import { Section } from "./_Section";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatCurrency, formatCurrencyToApi, formatPixKeyMasks, showToast } from "../../utils";
import { Flex, FormControl, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { Input } from "../../components/Forms/Input";

import { useSeller } from "../../hooks/useSeller";
import { useEffect, useRef, useState } from "react";
import { useAnticipation } from "../../hooks/useAnticipation";
import { Alert } from "../../components/Alert";
import { useParams } from "react-router-dom";
import { SelectButton } from "../../components/Forms/SelectButton";
import { InputButton } from "../../components/Forms/InputButton";
import { useSplit } from "../../hooks/useSlipt";
import { BusinessModel, BusinessModelType, PolicyInterval, Split } from "../../models/Split";
import { isValidCPF } from "../../utils/validationDocument";

const editFinancialSchema = yup.object().shape({
  capital: yup.string(),
  cashback: yup.string(),
});

const editAccountSchema = yup.object().shape({
  bank: yup.string(),
  issuer: yup.string(),
  account_number: yup.string(),
  bankDigit: yup.string(),
  issuerDigit: yup.string(),
  pixKey: yup.string()
});

const businessModelSchema = yup.object().shape({
  businessModelType: yup.string(),
  merchantId: yup.string()
});

const splitSchema = yup.object().shape({
  mmc: yup.number(),
  transferPolicyDay: yup.number(),
  transferPolicyEnabled: yup.boolean(),
  transferPolicyInterval: yup.string(),
});

export interface AccountSectionDataToApi {
  account?: {
    bank: string;
    issuer: string;
    number: string;
  }
  pixKey?: string;
}

interface FinancialSectionProps {
  preApprovedValue: {
    capital: number;
    cashback: number;
  };
  hasAnticipation: boolean;
  account: {
    bank: number;
    issuer: number;
    number: string;
    bankDigit: number;
    issuerDigit: number;
    pixKey: string;
    accountType: string;
    pixKeyType?: string;
    transferType?: string;
  }
  businessModelType: string;

}

export interface FinancialSectionData {
  //Financial
  capital?: number;
  cashback?: number;
  //Account
  bank?: string;
  issuer?: string;
  number?: string;
  pixKey?: string;
  pixKeyType?: string;
  bankDigit?: string;
  issuerDigit?: string;
  accountType?: 'CHECKING_ACCOUNT' | 'SAVINGS_ACCOUNT';
  //Business Model
  businessModelType?: BusinessModelType;
  merchantId?: string;
  //Split
  mcc?: number;
  transferPolicyDay?: number;
  transferPolicyInterval?: string;
  transferType?: string;
}

export function FinancialSection({
  preApprovedValue,
  account,
  hasAnticipation,
  businessModelType,
}: FinancialSectionProps) {
  let { id } = useParams();
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  const [editSection, setEditSection] = useState("");
  const formattedCapital = formatCurrency({ value: preApprovedValue?.capital });
  const formattedCashback = formatCurrency({ value: preApprovedValue?.cashback });
  const [editSchema, setEditSchema] = useState < any > (editFinancialSchema);
  const { patchPreApprovedValues, patchAccount } = useSeller();
  const { register, handleSubmit, setValue, watch } = useForm({
    resolver: yupResolver(editSchema),
  });
  const modalRef = useRef();
  const {
    antecipationModalState,
    titleMessage,
    antecipationSwitchState,
    setAntecipationModalState,
    setAntecipationState,
    setAntecipationSwitchState,
  } = useAnticipation(hasAnticipation);

  const {
    editSellerSplitConfig,
    editBusinessModelConfig,
    handleActiveSellerSplit,
    getSliptInitialData,
    getBusinessModelData,
    setSplitModalState,
    sellerBusinessModelData,
    sellerSplitData,
    splitSwitchState,
    splitModalState,
  } = useSplit();

  const onSubmit = handleSubmit(
    async ({
      capital,
      cashback,
      pixKeyType,
      pixKey,
      bank,
      accountType,
      issuer,
      issuerDigit,
      number,
      bankDigit,
      businessModelType,
      merchantId,
      mcc,
      transferPolicyDay,
      transferPolicyInterval,
      transferType
    }: FinancialSectionData) => {
      if (editSection === "financial") {
        try {
          const financialData = {
            capital: formatCurrencyToApi(capital),
            cashback: formatCurrencyToApi(cashback),
          };
          await patchPreApprovedValues(financialData, id);
          setEditSection("");
          showToast({
            message: "Informacoes editadas com sucesso",
            type: "success",
          });
          return;
        } catch (error: any) {
          setEditSection("");
          error?.response?.data?.errors?.map((error: any) =>
            showToast({
              type: "error",
              message: `${error?.code} - ${error?.description}`,
            })
          );
        }
        return
      }

      if (editSection === "account") {
        try {
          let account = { bank, issuer, number, issuerDigit, bankDigit, accountType, transferType };
          let accountData = {};
          Object.assign(accountData, { account, pixKey, pixKeyType, transferType });
          setEditSection("");
          await patchAccount(accountData, id);
          showToast({ message: "Informacoes editadas com sucesso", type: "success" });
        } catch (error: any) {
          setEditSection("");
          error?.response?.data?.errors?.map((error: any) => (
            showToast({
              type: "error",
              message: `${error?.code} - ${error?.description}`
            })
          ));
        }
        return
      }

      if (editSection === "businessModel") {
        try {
          const businessModelData: BusinessModel = {
            businessModelType: (businessModelType === "SPLIT_SUB_ACQUIRER" ||
              businessModelType === "SUB_ACQUIRER")
              ? businessModelType
              : "BARTE_ORCHESTRATION",
            merchantId: merchantId,
            subTypeOrchestration:
              (businessModelType !== "SPLIT_SUB_ACQUIRER" &&
                businessModelType !== "SUB_ACQUIRER")
                ? businessModelType
                : null,
          };
          await editBusinessModelConfig(id, businessModelData);
          setEditSection("");
          showToast({
            message: "Informacoes editadas com sucesso",
            type: "success",
          });
          return;
        } catch (error: any) {
          setEditSection("");
          error?.response?.data?.errors?.map((error: any) =>
            showToast({
              type: "error",
              message: `${error?.code} - ${error?.description}`,
            })
          );
        }
        return
      }

      if (editSection === "split") {
        try {
          const splitData: Split = {
            mcc,
            transferPolicyDay: transferPolicyInterval === "DAILY" ? 0 : transferPolicyDay,
            transferPolicyEnabled: splitSwitchState ?? false,
            transferPolicyInterval: transferPolicyInterval as PolicyInterval
          };
          await editSellerSplitConfig(id, splitData);
          setEditSection("");
          showToast({
            message: "Informações atualizadas com sucesso",
            type: "success",
          });
          return;
        } catch (error: any) {
          setEditSection("");
          error?.response?.data?.errors?.map((error: any) =>
            showToast({
              type: "error",
              message: `${error?.code} - ${error?.description}`,
            })
          );
        }
        return
      }
    }
  );

  function handleOpenSplitModal() {
    setSplitModalState(!splitModalState);
  }

  async function handleActiveSellerSplitFunction() {
    try {
      await handleActiveSellerSplit(id);
    } finally {
      setSplitModalState(false);
    }
  }

  function handleEditSection() {
    if (editSection === "financial") {
      setEditSchema(editFinancialSchema);
      return;
    }
    if (editSection === "account") {
      setEditSchema(editAccountSchema);
      return;
    }
    if (editSection === "businessModel") {
      setEditSchema(businessModelSchema);
      return;
    }
    if (editSection === "split") {
      setEditSchema(splitSchema);
      return;
    }
  }

  useEffect(() => {
    setValue("capital", formattedCapital);
    setValue("cashback", formattedCashback);
  });

  useEffect(() => {
    setValue("accountType", account?.accountType)
    setValue("bank", account?.bank)
    setValue("bankDigit", account?.bankDigit)
    setValue("issuer", account?.issuer)
    setValue("issuerDigit", account?.issuerDigit)
    setValue("number", account?.number)
    setValue("pixKey", account?.pixKey)
    setValue("pixKeyType", account?.pixKeyType)
    setValue("transferType", account?.transferType)
    // eslint-disable-next-line 
  }, [account])

  useEffect(() => {
    setValue("transferPolicyInterval", sellerSplitData?.transferPolicyInterval)
    setValue("transferPolicyEnabled", sellerSplitData?.transferPolicyEnabled)
    setValue("transferPolicyDay", sellerSplitData?.transferPolicyDay)
    setValue("mcc", sellerSplitData?.mcc)
    // eslint-disable-next-line
  }, [sellerSplitData])

  useEffect(() => {

    if (sellerBusinessModelData?.businessModelType === "BARTE_ORCHESTRATION") {
      setValue("businessModelType", sellerBusinessModelData?.subTypeOrchestration)
    } else {
      setValue("businessModelType", sellerBusinessModelData?.businessModelType)
    }

    setValue("merchantId", sellerBusinessModelData?.merchantId)
    // eslint-disable-next-line
  }, [sellerBusinessModelData])

  useEffect(() => {
    handleEditSection();
    getSliptInitialData(id);
    getBusinessModelData(id);
    // eslint-disable-next-line
  }, [editSection]);

  return (
    <>
      <Alert
        isOpen={splitModalState}
        onConfirm={handleActiveSellerSplitFunction}
        onClose={handleOpenSplitModal}
        title={sellerSplitData?.splitStatus === "INACTIVE"
          ? "Preencha todas as informações da Conta Primária antes de solicitar ativação de conta"
          : "Tem certeza que você deseja reenviar o cadastro?"
        }
        closeButtonText="Cancelar"
        messageBody={
          sellerSplitData?.splitStatus === "INACTIVE"
            ? (
              <UnorderedList>
                <ListItem>MCC</ListItem>
                <ListItem>Banco</ListItem>
                <ListItem>Agência</ListItem>
                <ListItem>Conta</ListItem>
                <ListItem>PIX</ListItem>
                <ListItem>PIX</ListItem>
                <ListItem>Frequência de repasse</ListItem>
                <ListItem>Nome</ListItem>
                <ListItem>Telefone</ListItem>
                <ListItem>E-mail</ListItem>
              </UnorderedList>
            )
            : "Confirme se todos os dados necessários foram corrigidos."
        }
        leastDestructiveRef={modalRef}
        confirmButtonText="Sim"
        messageBodyPosition="start"
      />
      <Alert
        isOpen={antecipationModalState}
        onConfirm={() => setAntecipationState(antecipationSwitchState)}
        onClose={() => {
          setAntecipationModalState(false);
          setAntecipationSwitchState(!antecipationSwitchState);
        }}
        title={`${titleMessage} cliente com antecipação?`}
        closeButtonText="Fechar"
        messageBody={
          antecipationSwitchState
            ? "Esse cliente ficará impedido de cancelar suas cobranças e registrá-las como pagas manualmente."
            : "Esse cliente voltará a poder cancelar suas cobranças e registrá-las como pagas manualmente."
        }
        leastDestructiveRef={modalRef}
      />

      {user?.email?.includes("@barte") && (
        <Section
          sectionTitle="Modelo de negócio"
          isEdit={editSection === "businessModel"}
          onClickEditButton={() => {
            setEditSection("businessModel");
          }}
          onClickConfirmButton={onSubmit}
        >
          <Flex gap={4}>
            <FormControl maxWidth="280px">
              <SelectButton
                name="businessModelType"
                disabled={editSection !== "businessModel"}
                label="Método de recebimento de cartão"
                variant="ghost"
                bgColor="#EDF1F7"
                h="27px"
                register={{ ...register("businessModelType") }}
              >
                <option value="SUB_ACQUIRER">Subadquirente</option>
                <option value="SPLIT_SUB_ACQUIRER">Split</option>
                <option value="BARTE_ORCHESTRATION_1">Barte Orquestração 1</option>
                <option value="BARTE_ORCHESTRATION_2">Barte Orquestração 2</option>
                <option value="BARTE_ORCHESTRATION_3">Barte Orquestração 3</option>
              </SelectButton>
            </FormControl>
            <FormControl maxWidth="280px">
              <InputButton
                label="Merchand ID"
                variant="ghost"
                name="merchantId"
                p="0"
                register={{ ...register("merchantId") }}
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                disabled={editSection !== "businessModel"}
              />
            </FormControl>
          </Flex>
        </Section>
      )}
      <Section
        sectionTitle="Conta primária"
        isEdit={editSection === "account"}
        onClickEditButton={() => {
          setEditSection("account");
        }}
        onClickConfirmButton={onSubmit}
      >
        <Flex gap={4} >
          <FormControl maxWidth="280px">
            <Input
              label="Banco"
              name="bank"
              p="0"
              register={{ ...register("bank") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              isDisabled={editSection !== "account"}
            />
          </FormControl>
          <FormControl
            maxWidth="280px"
            display="flex"
            alignItems="center"
            gap="2"
            position="relative"
          >
            <Input
              p="0"
              label="Agencia"
              name="issuer"
              register={{ ...register("issuer") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              isDisabled={editSection !== "account"}
            />
            <Text
              position="absolute"
              fontSize="10px"
              color="#8A94AB"
              bottom="-15px"
            >
              Se o dígito for X ou não existir, digite 0.
            </Text>
            <Input
              p="0"
              label="Dig."
              name="issuerDigit"
              register={{ ...register("issuerDigit") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              maxW="60px"
              isDisabled={editSection !== "account"}
              maxLength={1}
            />
          </FormControl>
          <FormControl
            maxWidth="280px"
            display="flex"
            alignItems="center"
            gap="2"
          >
            <Input
              p="0"
              label="Conta"
              name="number"
              register={{ ...register("number") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              isDisabled={editSection !== "account"}
            />
            <Input
              p="0"
              label="Dig."
              name="bankDigit"
              register={{ ...register("bankDigit") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              maxW="60px"
              isDisabled={editSection !== "account"}
              maxLength={1}
            />
          </FormControl>
          <FormControl maxWidth="280px">
            <Input
              p="0"
              label="Pix"
              name="pixKey"
              mask={formatPixKeyMasks(watch("pixKeyType"), watch("pixKey") || account?.pixKey)}
              maxLength={isValidCPF(account?.pixKey) ? 18 : 99}
              register={{ ...register("pixKey") }}
              _disabled={{
                bg: "transparent",
                border: "none",
              }}
              isDisabled={editSection !== "account"}
            />
          </FormControl>
        </Flex>
        <Flex gap={4} >
          <FormControl
            maxWidth="280px"
            marginTop="20px"
          >
            <SelectButton
              name="pixKeyType"
              disabled={editSection !== "account"}
              label="Tipo de chave pix"
              variant="ghost"
              bgColor="#EDF1F7"
              h="27px"
              register={{ ...register("pixKeyType") }}
            >
              <option value="ALLEATORY_KEY">Chave Aleatoria</option>
              <option value="DOCUMENT">{isValidCPF(account?.pixKey) ? "CPF" : "CNPJ"}</option>
              <option value="EMAIL">E-mail</option>
              <option value="PHONE">Telefone</option>
            </SelectButton>
          </FormControl>
          <FormControl
            maxWidth="280px"
            marginTop="20px"
          >
            <SelectButton
              name="accountType"
              disabled={editSection !== "account"}
              label="Tipo de conta"
              variant="ghost"
              bgColor="#EDF1F7"
              h="27px"
              register={{ ...register("accountType") }}
            >
              <option value="CHECKING_ACCOUNT">Conta corrente</option>
              <option value="SAVINGS_ACCOUNT">Conta poupança</option>
            </SelectButton>
          </FormControl>
          <FormControl
            maxWidth="280px"
            marginTop="20px"
          >
            <SelectButton
              name="transferType"
              disabled={editSection !== "account"}
              label="Tipo de transferência"
              variant="ghost"
              bgColor="#EDF1F7"
              h="27px"
              register={{ ...register("transferType") }}
            >
              <option value="PIX">Pix</option>
              <option value="BANK_ACCOUNT">Conta Bancária</option>
            </SelectButton>
          </FormControl>
        </Flex>
      </Section>
      {user?.email?.includes("@barte") && (
        <Section
          sectionTitle="Financiamento"
          isEdit={editSection === "financial"}
          onClickEditButton={() => {
            setEditSection("financial");
          }}
          onClickConfirmButton={onSubmit}
        >
          <Flex gap={4}>
            <FormControl maxWidth="280px">
              <Input
                label="Capital Pré-aprovado"
                defaultValue={formattedCapital}
                name="capital"
                p="0"
                register={{ ...register("capital") }}
                mask="currency"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "financial"}
              />
            </FormControl>
            <FormControl maxWidth="280px">
              <Input
                label="Cashback"
                name="cashback"
                defaultValue={formattedCashback}
                p="0"
                register={{ ...register("cashback") }}
                mask="currency"
                _disabled={{
                  bg: "transparent",
                  border: "none",
                }}
                isDisabled={editSection !== "financial"}
              />
            </FormControl>
          </Flex>
        </Section>
      )}

    </>
  );
}
